/**
 * Full-screen viewer.
 *
 * The rule from the brief that shapes everything here: the original is never
 * the display path. Opening a photo loads the ~250 KB preview, and the 4096 px
 * variant is requested only if someone actually zooms past 100%. Neighbours are
 * preloaded — as previews, never originals — so arrowing through an album feels
 * instant without pulling megabytes for photos nobody looks at.
 *
 * Three photos are mounted at once (previous, current, next) side by side in a
 * track. That is what makes a drag show the real neighbouring photo following
 * your finger instead of a blank gap that fills in after you let go. Gesture
 * handling lives in `useLightboxGestures`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Avatar } from "../../components/ui/Avatar.js";
import { formatBytes, shouldWarnBeforeOriginal } from "@fc/core";
import { trpcClient } from "../../lib/trpc.js";
import { useToast } from "../../components/ui/Toast.js";
import { imageAccept } from "../../lib/imageSupport.js";
import { acquireDecryptedUrl, release } from "../../lib/mediaCache.js";
import {
  isApplePhone,
  saveStrategy,
  shareFiles,
  withImageExtension,
} from "../../lib/saveToPhone.js";
import { useDecryptedUrl, type MediaRef } from "../../lib/useDecryptedUrl.js";
import { useLightboxGestures } from "./useLightboxGestures.js";
import { PhotoComments } from "../wall/PhotoComments.js";
import type { GalleryItem } from "./useGallery.js";
import "./Lightbox.css";

export interface LightboxProps {
  groupId: string;
  items: GalleryItem[];
  index: number;
  canDownloadOriginal: boolean;
  canComment: boolean;
  /** The grid's already-decoded thumbnails, used to fill the frame instantly. */
  thumbRefs?: ReadonlyMap<string, MediaRef>;
  /**
   * Who is in this group, so a photograph can say who added it.
   *
   * A shared album is a pile of everybody's photographs, and “whose is this?”
   * is a question the details panel could not answer — which also made it
   * impossible to tell, from the photograph itself, whether it was yours to
   * delete.
   */
  members?: ReadonlyMap<string, { name: string; avatar?: string | null }>;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onToggleFavorite: (fileId: string, favorited: boolean) => void;
  /** Opens the editor on this file. Absent where the viewer cannot add files. */
  onEdit?: (fileId: string) => void;
}

type Variant =
  "preview" | "preview_hq" | "original" | "video_720" | "video_1080" | "poster";

export function Lightbox({
  groupId,
  items,
  index,
  canDownloadOriginal,
  canComment,
  thumbRefs,
  members,
  onClose,
  onNavigate,
  onToggleFavorite,
  onEdit,
}: LightboxProps) {
  const item = items[index];
  /* Null for a photograph whose uploader has since left the group. */
  const uploader = item?.uploaderId
    ? (members?.get(item.uploaderId) ?? null)
    : null;
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [showInfo, setShowInfo] = useState(false);
  const [chrome, setChrome] = useState(true);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<Element | null>(null);
  const highRes = useRef(new Set<string>());
  const { toast } = useToast();

  /** Signed URLs and their keys, cached in-module so back-and-forth is free. */
  const load = useCallback(
    async (
      fileId: string,
      variant: Variant = "preview",
      intent: "download" | "view" = "download",
    ): Promise<MediaRef | null> => {
      // The intent is part of the identity: a URL signed for viewing and one
      // signed for downloading differ, and handing back the wrong one is the
      // whole bug this parameter exists for.
      const cacheKey = `${fileId}:${variant}:${intent}`;
      const cached = urlCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) return cached;

      const result = await trpcClient.media.url.query({
        groupId,
        fileId,
        variant,
        intent,
        accept: await imageAccept(),
      });
      if (!result.url) return null;

      const source: CachedRef = {
        url: result.url,
        wrappedFileKey: result.wrappedFileKey ?? null,
        expiresAt:
          Date.now() + result.expiresInSeconds * 1000 - URL_EXPIRY_MARGIN_MS,
      };
      urlCache.set(cacheKey, source);
      return source;
    },
    [groupId],
  );

  /*
   * Every decrypted object URL this viewer is displaying, by the slot it fills.
   *
   * Kept because a blob lives until it is revoked, and the viewer is the one
   * place somebody pages through a hundred full-size photographs in a minute.
   * Without giving each one back, a long browse would hold every preview it
   * ever showed.
   */
  const retained = useRef(new Map<string, string>());

  /**
   * Open a slot's bytes and put the result on screen.
   *
   * Returns the renderable URL so callers can push it through an `Image` for
   * decoding, which is what makes a neighbouring slide appear instantly rather
   * than one frame late.
   */
  const remember = useCallback(
    async (
      slot: string,
      source: MediaRef | null,
      contentType = "image/webp",
    ) => {
      if (!source) return null;

      const ready = await acquireDecryptedUrl({
        url: source.url,
        wrappedFileKey: source.wrappedFileKey,
        groupId,
        /*
         * Derivatives are WebP; an original keeps whatever it was shot as, and
         * saying so matters on a phone. A long press offers "Add to Photos", and
         * iOS will not accept a WebP — so a blob mislabelled as one turns the
         * only way to save a picture on that platform into a silent failure.
         */
        contentType,
      }).catch(() => null);
      if (!ready) return null;

      const previous = retained.current.get(slot);
      if (previous === ready) {
        // Already displaying exactly this. Give the duplicate retain straight
        // back, or the count never reaches zero and it is never evictable.
        release(ready);
        return ready;
      }
      if (previous) release(previous);
      retained.current.set(slot, ready);

      setUrls((current) =>
        current[slot] === ready ? current : { ...current, [slot]: ready },
      );
      return ready;
    },
    [groupId],
  );

  /* Closing the viewer gives everything back. */
  useEffect(() => {
    const held = retained.current;
    return () => {
      for (const url of held.values()) release(url);
      held.clear();
    };
  }, []);

  /** The three photos that are mounted: what you can see, and what you can reach. */
  const window3 = useMemo(
    () =>
      [-1, 0, 1]
        .map((offset) => ({ offset, position: index + offset }))
        .filter((slot) => slot.position >= 0 && slot.position < items.length)
        .map((slot) => ({ ...slot, item: items[slot.position]! })),
    [index, items],
  );

  /*
   * Which slides are mounted, as a value that only changes when the answer
   * changes.
   *
   * `window3` is rebuilt whenever `items` gets a new array identity, and the
   * gallery re-fetches on a timer while anything is still processing. Keying
   * the effect on the object meant it re-ran on every poll — and its cleanup
   * cancelled the in-flight URL requests, so the photo never got a src and the
   * viewer sat on a black frame. Joining the ids makes the dependency describe
   * the slides rather than the array that happened to contain them.
   */
  const windowKey = window3
    .map((slot) => `${slot.item.id}:${String(slot.offset)}`)
    .join("|");

  /*
   * Slots outside the mounted window are given back as the viewer moves.
   *
   * Three slides are on screen; anything further away is scrollback, which the
   * cache is free to keep or drop. Holding it here would take that decision
   * away and turn a long browse into a hundred previews that never leave.
   */
  useEffect(() => {
    const live = new Set(
      windowKey.split("|").map((slot) => slot.split(":")[0]!),
    );

    for (const [slot, url] of [...retained.current]) {
      if (live.has(slot.split(":")[0]!)) continue;
      release(url);
      retained.current.delete(slot);
    }
    setUrls((current) => {
      const next: Record<string, string> = {};
      for (const [slot, url] of Object.entries(current)) {
        if (retained.current.has(slot)) next[slot] = url;
      }
      return next;
    });
  }, [windowKey]);

  /*
   * Resolve every mounted slide, not just the current one.
   *
   * The neighbours have to be real, decoded images before the drag starts —
   * that is the entire point of mounting them. Each is pushed through an
   * `Image` so the browser has the bytes, not merely the URL.
   *
   * Nothing here is cancelled on re-run. Recording a URL is idempotent and
   * keyed by file, so a response that arrives after the user has moved on is
   * still correct — and discarding it is what caused the blank frames.
   */
  useEffect(() => {
    for (const { item: slide, offset } of window3) {
      if (slide.kind === "video") {
        void load(slide.id, "poster").then((source) =>
          remember(`${slide.id}:poster`, source),
        );
        if (offset !== 0) continue;

        const preferred =
          window.innerWidth >= 1280 ? "video_1080" : "video_720";
        void (async () => {
          const primary =
            (await load(slide.id, preferred).catch(() => null)) ??
            (await load(slide.id, "video_720").catch(() => null));
          await remember(slide.id, primary);
        })();
        continue;
      }

      void load(slide.id).then(async (source) => {
        const ready = await remember(slide.id, source);
        if (!ready) return;
        // Decoding here is what makes the neighbour appear the instant the drag
        // reaches it, rather than one frame after.
        const image = new Image();
        image.decoding = "async";
        image.src = ready;
      });
    }
    // Keyed on `windowKey` rather than `window3`: the two describe the same
    // slides, but only one of them keeps its identity across a re-render.
  }, [windowKey, load, remember]);

  /**
   * Forget a signed URL that the browser could not load, and fetch a fresh one.
   *
   * Signed URLs are cached for the life of the page but only valid for about an
   * hour, so a tab left open overnight will hand the `<img>` a URL the server
   * now rejects — which renders as a black frame with nothing to explain it.
   */
  const refreshUrl = useCallback(
    (fileId: string) => {
      /*
       * Every entry for this file, by prefix rather than by naming the
       * variants. The key carries the intent as well now, so an explicit list
       * silently stops matching the moment another one is added — and the
       * symptom would be a stale URL surviving exactly the refresh meant to
       * clear it.
       */
      for (const key of urlCache.keys()) {
        if (key.startsWith(`${fileId}:`)) urlCache.delete(key);
      }
      const stale = retained.current.get(fileId);
      if (stale) {
        release(stale);
        retained.current.delete(fileId);
      }
      setUrls((current) => {
        const next = { ...current };
        delete next[fileId];
        return next;
      });
      void load(fileId).then((source) => remember(fileId, source));
    },
    [load, remember],
  );

  /**
   * Zooming past ~1.2× is the signal to fetch the 4096 px variant. Requested
   * once per file: the HQ URL replaces the preview in `urls`, and asking again
   * would only re-download what is already on screen.
   */
  const onZoomChange = useCallback(
    (scale: number) => {
      if (!item || scale <= 1.2) return;
      if ((item.width ?? 0) <= 2048 || highRes.current.has(item.id)) return;

      highRes.current.add(item.id);
      void (async () => {
        await trpcClient.media.requestHighRes
          .mutate({ groupId, fileId: item.id })
          .catch(() => {});
        const hq = await load(item.id, "preview_hq").catch(() => null);
        await remember(item.id, hq);
      })();
    },
    [item, groupId, load, remember],
  );

  const gestures = useLightboxGestures({
    index,
    count: items.length,
    disabled: showInfo,
    onNavigate,
    onClose,
    onZoomChange,
    onTap: (event) => {
      // A tap beside the photo is the desktop "click the backdrop" gesture; a
      // tap on the photo itself toggles the chrome, as a phone viewer does.
      const onPhoto = (event.target as HTMLElement).closest(
        ".fc-lightbox__frame",
      );
      if (onPhoto) setChrome((value) => !value);
      else onClose();
    },
  });

  const { resetZoom } = gestures;
  useEffect(() => {
    resetZoom(false);
  }, [index, resetZoom]);

  // Focus management: trap inside the dialog, restore on close.
  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    closeRef.current?.focus();
    const { overflow, overscrollBehavior } = document.body.style;
    document.body.style.overflow = "hidden";
    // Without this, a swipe that starts inside the viewer can still trigger the
    // browser's pull-to-refresh on Android.
    document.body.style.overscrollBehavior = "none";

    return () => {
      document.body.style.overflow = overflow;
      document.body.style.overscrollBehavior = overscrollBehavior;
      (previouslyFocused.current as HTMLElement | null)?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          event.preventDefault();
          onClose();
          break;
        case "ArrowLeft":
          event.preventDefault();
          if (index > 0) onNavigate(index - 1);
          break;
        case "ArrowRight":
          event.preventDefault();
          if (index < items.length - 1) onNavigate(index + 1);
          break;
        case "+":
        case "=":
          event.preventDefault();
          gestures.zoomBy(1.5);
          break;
        case "-":
          event.preventDefault();
          gestures.zoomBy(1 / 1.5);
          break;
        case "0":
          event.preventDefault();
          gestures.resetZoom(true);
          break;
        case "i":
          setShowInfo((value) => !value);
          break;
        case "f":
          if (item) onToggleFavorite(item.id, !item.favorited);
          break;
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    index,
    items.length,
    item,
    onClose,
    onNavigate,
    onToggleFavorite,
    gestures,
  ]);

  const downloadOriginal = useCallback(async () => {
    if (!item) return;
    try {
      const result = await trpcClient.media.url.query({
        groupId,
        fileId: item.id,
        variant: "original",
      });
      if (!result.url) return;

      /*
       * Navigating to the signed URL used to be the whole of this, and for a
       * sealed file it downloads ciphertext — a file named like a photograph
       * that nothing on the person's computer can open. The bytes have to come
       * through the same path everything else does.
       */
      if (!result.wrappedFileKey) {
        window.location.href = result.url;
        return;
      }

      const objectUrl = await acquireDecryptedUrl({
        url: result.url,
        wrappedFileKey: result.wrappedFileKey,
        groupId,
        contentType: item.mime,
      });

      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      /* With the extension the bytes actually call for: Android decides
         whether a download is a photograph by extension and nothing else. */
      anchor.download = withImageExtension(item.name, item.mime);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      // Given back on the next turn: revoking immediately can beat the browser
      // to reading the blob.
      setTimeout(() => release(objectUrl), 60_000);
    } catch (error) {
      toast(
        error instanceof Error
          ? error.message
          : "Couldn't start that download.",
        {
          variant: "danger",
        },
      );
    }
  }, [groupId, item, toast]);

  /**
   * Show the photograph at its own resolution.
   *
   * A button because a phone has no other way to ask. Pinch-zoom already
   * triggers this, but only past a threshold, and only once somebody has
   * guessed that zooming is what loads more detail — which is not discoverable
   * and is fiddly on a moving image.
   *
   * For a sealed photograph the full-size version *is* the original: there is
   * no 4096px rung, because nothing on the server could have made one. The
   * browser holds the key, so it fetches the original and opens it.
   */
  const [fullSize, setFullSize] = useState<string | null>(null);

  /** Put a file back to the version the viewer normally shows. */
  const restorePreview = useCallback(
    async (fileId: string, kind: "photo" | "video") => {
      const source =
        kind === "video"
          ? ((await load(
              fileId,
              window.innerWidth >= 1280 ? "video_1080" : "video_720",
            ).catch(() => null)) ??
            (await load(fileId, "video_720").catch(() => null)))
          : await load(fileId, "preview").catch(() => null);
      await remember(fileId, source);
    },
    [load, remember],
  );

  /*
   * Leaving a photo returns it to its preview.
   *
   * Without this the swap was one-way: the viewer kept showing the original
   * until the page was reloaded, so "see it full size" was a decision you could
   * not take back, and every file you had ever opened that way stayed decoded
   * in memory for the rest of the session.
   */
  const fullSizeRef = useRef<{ id: string; kind: "photo" | "video" } | null>(
    null,
  );
  useEffect(() => {
    const held = fullSizeRef.current;
    if (!held || held.id === item?.id) return;
    fullSizeRef.current = null;
    setFullSize(null);
    void restorePreview(held.id, held.kind);
  }, [item?.id, restorePreview]);

  const showFullResolution = useCallback(async () => {
    if (!item) return;

    // A toggle, not a one-way door.
    if (fullSize === item.id) {
      fullSizeRef.current = null;
      setFullSize(null);
      await restorePreview(item.id, item.kind);
      return;
    }

    setFullSize(item.id);
    fullSizeRef.current = { id: item.id, kind: item.kind };
    try {
      /*
       * Asked for as something to look at, not something to download.
       *
       * The server answers a plain original request with
       * `Content-Disposition: attachment`, which an `<img>` ignores — so the
       * photograph appeared at full resolution and looked entirely fine, while
       * iOS's long-press "Save to Photos" re-fetched the same URL, was handed a
       * download, and failed, reported as a connection problem.
       *
       * Worth keeping, and worth being clear about what it covers: the long
       * press only re-fetches a URL when there is one to re-fetch, which is the
       * unencrypted path. A sealed photograph is shown from a `blob:` URL over
       * bytes decrypted in this tab, and no signing or disposition makes that
       * saveable. See `saveToPhotos` below for the route that works.
       */
      const source =
        (await load(item.id, "original", "view").catch(() => null)) ??
        (await load(
          item.id,
          item.kind === "video" ? "video_1080" : "preview_hq",
        ).catch(() => null));
      if (!source) {
        throw new Error(
          item.kind === "video"
            ? "There is no higher-quality version of this video."
            : "There is no larger version of this photo.",
        );
      }
      await remember(item.id, source, item.mime);
    } catch (error) {
      setFullSize(null);
      fullSizeRef.current = null;
      toast(
        error instanceof Error
          ? error.message
          : `Couldn't load the full-size ${item.kind === "video" ? "video" : "photo"}.`,
        { variant: "danger" },
      );
    }
  }, [item, fullSize, load, remember, restorePreview, toast]);

  /*
   * Which route into the camera roll this device actually has.
   *
   * Fixed for the life of the page, and only used to choose behaviour and
   * wording — never to decide whether saving is offered at all.
   */
  const strategy = useMemo(saveStrategy, []);

  /**
   * Save the photograph to the phone.
   *
   * On an iPhone, holding the picture and choosing “Save to Photos” fails
   * with “no internet connection”, and the reason is not the format: iOS
   * saves JPEG perfectly well, and JFIF *is* JPEG. It is that the long press
   * makes the phone re-fetch the image’s address, and for an encrypted
   * photograph that address is a `blob:` URL over bytes this tab decrypted in
   * memory. There is nothing at the other end for iOS to fetch, and it
   * reports that as a network failure.
   *
   * Two earlier fixes chased the same message — a download disposition, then
   * an expired signature — and both were real, but both only ever applied to
   * the unencrypted path, where the address really is a server URL. For a
   * sealed photograph the long press cannot work however the URL is signed.
   *
   * The route that does work is the share sheet, whose “Save Image” writes to
   * Photos. The app already knew this and used it for saving a whole album;
   * it was simply never offered for a single picture, which is how most
   * people save one.
   */
  const saveToPhotos = useCallback(async () => {
    if (!item) return;

    if (strategy === "longpress") {
      /* No share sheet: the long press is genuinely all that is left, so say
         so rather than offering a button that cannot work. */
      toast("Press and hold the photo, then choose Save to Photos.");
      return;
    }

    try {
      /*
       * Prefer what is already on screen at full size: those bytes are in
       * memory, so reading them back is local and the tap’s activation
       * survives it. `share()` spends that activation, and a network fetch in
       * front of it is the classic “works on desktop, fails on iPhone”.
       */
      let objectUrl = fullSize === item.id ? (urls[item.id] ?? null) : null;
      let borrowed = false;

      if (!objectUrl) {
        const result = await trpcClient.media.url.query({
          groupId,
          fileId: item.id,
          variant: "original",
          intent: "view",
        });
        if (!result.url) throw new Error("That photo could not be fetched.");

        objectUrl = await acquireDecryptedUrl({
          url: result.url,
          wrappedFileKey: result.wrappedFileKey ?? null,
          groupId,
          contentType: item.mime,
        });
        borrowed = true;
      }

      try {
        const blob = await (await fetch(objectUrl)).blob();
        const type = blob.type || item.mime || "image/jpeg";

        /*
         * A `File`, not a `Blob`: the sheet uses the name, and an unnamed one
         * arrives in Photos untitled. The extension has to match the bytes
         * too — Android classifies a download as a photograph by extension
         * alone, so a name without one never reaches the gallery.
         */
        const file = new File([blob], withImageExtension(item.name, type), {
          type,
          lastModified: Date.now(),
        });

        if (strategy === "share") {
          const outcome = await shareFiles([file]);
          if (outcome === "unsupported") {
            toast("Press and hold the photo, then choose Save to Photos.");
          } else if (outcome === "failed") {
            toast("That photo could not be shared.", { variant: "danger" });
          }
          /* “shared” and “cancelled” say nothing: the API never reports which
             target was chosen, so claiming it was saved would be a guess. */
          return;
        }

        /* Android and desktop: an ordinary download, which Android does index
           into the gallery as long as the name carries an extension. */
        const href = URL.createObjectURL(file);
        const anchor = document.createElement("a");
        anchor.href = href;
        anchor.download = file.name;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(href), 30_000);
      } finally {
        /* Only what this call took out. The displayed copy belongs to the
           slide and is released when the slide changes. */
        if (borrowed && objectUrl) release(objectUrl);
      }
    } catch (error) {
      toast(
        error instanceof Error
          ? error.message
          : "That photo could not be saved.",
        { variant: "danger" },
      );
    }
  }, [item, groupId, strategy, fullSize, urls, toast]);

  if (!item) return null;

  return createPortal(
    <div
      className="fc-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={item.name}
    >
      <header
        className="fc-lightbox__bar fc-lightbox__bar--top"
        data-hidden={!chrome || undefined}
      >
        <button
          ref={closeRef}
          type="button"
          className="fc-lightbox__btn"
          onClick={onClose}
          aria-label="Close"
        >
          <Icon path="M6 6l12 12M18 6L6 18" />
        </button>

        <div className="fc-lightbox__title">
          <span className="fc-lightbox__name">{item.name}</span>
          <span className="fc-lightbox__meta fc-tabular">
            {new Date(item.takenAt).toLocaleDateString(undefined, {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
            {" · "}
            {index + 1}/{items.length}
          </span>
        </div>

        <div className="fc-lightbox__actions">
          <button
            type="button"
            className="fc-lightbox__btn"
            aria-label={
              item.favorited ? "Remove from favourites" : "Add to favourites"
            }
            aria-pressed={item.favorited}
            data-active={item.favorited || undefined}
            onClick={() => onToggleFavorite(item.id, !item.favorited)}
          >
            <Icon
              path="M12 21s-8-4.9-8-10.4A4.6 4.6 0 0 1 12 7a4.6 4.6 0 0 1 8 3.6C20 16.1 12 21 12 21z"
              filled={item.favorited}
            />
          </button>

          {/*
            Shown only where the original can actually be fetched.

            For a sealed photograph the original *is* the full-resolution
            version — there is no 4096px rung, because nothing on the server
            could have produced one from ciphertext. So a group that has turned
            originals off has genuinely not got a higher resolution to offer,
            and a button that always failed would be worse than no button.
          */}
          {canDownloadOriginal && (
            <button
              type="button"
              className="fc-lightbox__btn"
              aria-label={
                fullSize === item.id
                  ? "Back to the smaller version"
                  : `View this ${item.kind === "video" ? "video" : "photo"} at full resolution`
              }
              aria-pressed={fullSize === item.id}
              data-active={fullSize === item.id || undefined}
              onClick={() => void showFullResolution()}
              title={
                fullSize === item.id ? "Back to smaller" : "Full resolution"
              }
            >
              <Icon path="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </button>
          )}

          {/*
            Photos only. There is nothing here that edits a video, and a button
            that opened an editor with no working tools would be worse than an
            absent one.
          */}
          {onEdit && item.kind === "photo" && (
            <button
              type="button"
              className="fc-lightbox__btn"
              aria-label="Edit this photo"
              title="Edit"
              onClick={() => onEdit(item.id)}
            >
              <Icon path="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17zM14 6l4 4" />
            </button>
          )}

          <button
            type="button"
            className="fc-lightbox__btn"
            aria-label="Photo details"
            aria-pressed={showInfo}
            data-active={showInfo || undefined}
            onClick={() => setShowInfo((value) => !value)}
          >
            <Icon path="M12 16v-5M12 8h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
          </button>
        </div>
      </header>

      <div
        className="fc-lightbox__stage"
        ref={gestures.stageRef}
        {...gestures.handlers}
      >
        <div className="fc-lightbox__track" ref={gestures.trackRef}>
          {window3.map(({ offset, item: slide }) => (
            <div
              key={slide.id}
              className="fc-lightbox__slide"
              style={{
                transform: `translate3d(${String(offset * 100)}%, 0, 0)`,
              }}
              aria-hidden={offset !== 0 || undefined}
            >
              <Slide
                item={slide}
                url={urls[slide.id] ?? null}
                poster={urls[`${slide.id}:poster`] ?? null}
                thumbSource={thumbRefs?.get(slide.id) ?? null}
                groupId={groupId}
                isCurrent={offset === 0}
                frameRef={offset === 0 ? gestures.frameRef : undefined}
                onReload={refreshUrl}
              />
            </div>
          ))}
        </div>
      </div>

      {index > 0 && (
        <button
          type="button"
          className="fc-lightbox__nav fc-lightbox__nav--prev"
          data-hidden={!chrome || undefined}
          aria-label="Previous photo"
          onClick={() => onNavigate(index - 1)}
        >
          <Icon path="M15 5l-7 7 7 7" />
        </button>
      )}
      {index < items.length - 1 && (
        <button
          type="button"
          className="fc-lightbox__nav fc-lightbox__nav--next"
          data-hidden={!chrome || undefined}
          aria-label="Next photo"
          onClick={() => onNavigate(index + 1)}
        >
          <Icon path="M9 5l7 7-7 7" />
        </button>
      )}

      {showInfo && (
        <aside className="fc-lightbox__info">
          <h3>Details</h3>
          <dl>
            <dt>File</dt>
            <dd>{item.name}</dd>
            <dt>Taken</dt>
            <dd>{new Date(item.takenAt).toLocaleString()}</dd>
            {item.width && item.height && (
              <>
                <dt>Dimensions</dt>
                <dd className="fc-tabular">
                  {item.width} × {item.height}
                </dd>
              </>
            )}
            <dt>Size</dt>
            <dd className="fc-tabular">{formatBytes(item.sizeBytes)}</dd>
            {uploader && (
              <>
                <dt>Added by</dt>
                <dd className="fc-lightbox__uploader">
                  <Avatar
                    name={uploader.name}
                    src={uploader.avatar ?? undefined}
                    size="sm"
                    decorative
                  />
                  {uploader.name}
                </dd>
              </>
            )}
          </dl>

          {canDownloadOriginal && (
            <div className="fc-lightbox__download">
              <p className="fc-lightbox__warning">
                {shouldWarnBeforeOriginal(item.sizeBytes)
                  ? `The original is ${formatBytes(item.sizeBytes)} and may use a lot of data.`
                  : "Download the file exactly as it was uploaded."}
              </p>
              {/*
                First, and on a phone the one most people want: this is the
                only route into the camera roll. “Download” below it puts the
                file in Files on iOS and Downloads on Android — useful, but
                not the same thing, and not what “save this photo” means.
              */}
              {item.kind === "photo" && (
                <button
                  type="button"
                  className="fc-lightbox__download-btn"
                  onClick={() => void saveToPhotos()}
                >
                  {isApplePhone() ? "Save to Photos" : "Save to device"}
                </button>
              )}
              <button
                type="button"
                className="fc-lightbox__download-btn"
                onClick={downloadOriginal}
              >
                Download original · {formatBytes(item.sizeBytes)}
              </button>
            </div>
          )}

          {/* Keyed on the file so switching photos with the panel open starts a
              fresh thread rather than showing the previous photo's replies. */}
          <PhotoComments
            key={item.id}
            groupId={groupId}
            fileId={item.id}
            canComment={canComment}
          />
        </aside>
      )}
    </div>,
    document.body,
  );
}

interface SlideProps {
  item: GalleryItem;
  url: string | null;
  poster: string | null;
  /** Already decoded by the grid, so it can stand in instantly. */
  thumbSource: MediaRef | null;
  groupId: string;
  isCurrent: boolean;
  frameRef?: React.Ref<HTMLDivElement>;
  onReload: (fileId: string) => void;
}

function Slide({
  item,
  url,
  poster,
  thumbSource,
  groupId,
  isCurrent,
  frameRef,
  onReload,
}: SlideProps) {
  /*
   * The grid already decrypted this thumbnail, so asking again is a cache hit
   * and a retain — no second fetch, no second decryption. That is what lets the
   * blurred stand-in appear in the same frame the viewer opens.
   */
  const thumbUrl = useDecryptedUrl(thumbSource, groupId).url;

  /*
   * A file the server has not finished with has no variant to show, and
   * "Loading…" would sit there forever implying the network is slow. The grid
   * already distinguishes these two states; so should the viewer.
   */
  if (
    item.status === "failed" ||
    item.status === "processing" ||
    item.status === "uploading"
  ) {
    return (
      <div className="fc-lightbox__frame" ref={frameRef}>
        <div className="fc-lightbox__placeholder" data-state={item.status}>
          {item.status === "failed" ? (
            <>
              <strong>We couldn&apos;t process this file</strong>
              {/*
                The server's own words where it has them.

                The fallback below is the right sentence for a photograph a
                decoder rejected and the wrong one for a photograph that was
                never decoded at all — which is what a background service being
                unreachable produces. Guessing "it may be corrupt" about an
                intact file, with nothing offered to do about it, sends people
                looking for a fault that is not there.
              */}
              <span>
                {item.processingError ??
                  "It may be corrupt. The original is still stored safely."}
              </span>
            </>
          ) : (
            <>
              <span className="fc-lightbox__spinner" aria-hidden="true" />
              <strong>Still processing</strong>
              <span>{item.name} will appear here once it is ready.</span>
            </>
          )}
        </div>
      </div>
    );
  }

  if (item.kind === "video") {
    return (
      <div className="fc-lightbox__frame" ref={frameRef}>
        {isCurrent && url ? (
          <video
            key={item.id}
            className="fc-lightbox__video"
            src={url}
            poster={poster ?? undefined}
            controls
            playsInline
            // `metadata` rather than `auto`: with the moov atom at the front the
            // browser needs only the header to start, and preloading a whole
            // clip the user may skip past is exactly the egress we avoid.
            preload="metadata"
          />
        ) : (poster ?? thumbUrl) ? (
          <img
            className="fc-lightbox__image"
            src={poster ?? thumbUrl!}
            alt={item.name}
            decoding="async"
          />
        ) : (
          <div className="fc-lightbox__status" aria-live="polite">
            <span className="fc-lightbox__spinner" aria-hidden="true" />
            <span>Loading video…</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <PhotoSlide
      item={item}
      url={url}
      thumbUrl={thumbUrl}
      frameRef={frameRef}
      onReload={onReload}
    />
  );
}

interface PhotoSlideProps {
  item: GalleryItem;
  url: string | null;
  thumbUrl: string | null;
  frameRef?: React.Ref<HTMLDivElement>;
  onReload: (fileId: string) => void;
}

/**
 * One photo, with the two states the old version had no way to express.
 *
 * The grid's thumbnail is already decoded by the time anyone opens the viewer,
 * so it fills the frame immediately, scaled up and blurred, and the full
 * preview fades in over it. Previously the frame was empty until the preview
 * had both arrived and decoded, which on a phone is long enough to read as a
 * bug — and indistinguishable from one when something actually did go wrong.
 */
function PhotoSlide({
  item,
  url,
  thumbUrl,
  frameRef,
  onReload,
}: PhotoSlideProps) {
  /*
   * Track *which* URL loaded, rather than a loading flag that gets reset.
   *
   * The previous version reset the flag to "loading" in an effect keyed on the
   * URL. Effects run after the browser has already had a chance to fire `load`
   * — which it does immediately for a cached image — so the sequence was:
   * image loads, state becomes ready, then the mount effect resets it to
   * loading. Nothing ever set it back, so the photo stayed invisible behind its
   * blurred stand-in forever. Comparing URLs needs no reset and cannot race.
   */
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const retried = useRef(false);

  const ready = url !== null && loadedUrl === url;
  const failed = url !== null && failedUrl === url;
  const loading = !ready && !failed;

  /*
   * A cached image can finish decoding before React attaches `onLoad`, so that
   * event alone is not enough — the ref catches anything already complete by
   * the time the node exists.
   */
  const captureIfAlreadyLoaded = (node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth > 0 && url) setLoadedUrl(url);
  };

  const state = failed ? "error" : ready ? "ready" : "loading";

  return (
    <div
      className="fc-lightbox__frame"
      ref={frameRef}
      data-loading={loading || undefined}
    >
      {thumbUrl && state !== "error" && (
        <img
          className="fc-lightbox__preview-blur"
          src={thumbUrl}
          alt=""
          aria-hidden="true"
          decoding="async"
          draggable={false}
        />
      )}

      {url && state !== "error" && (
        <img
          key={url}
          ref={captureIfAlreadyLoaded}
          className="fc-lightbox__image"
          src={url}
          alt={item.caption ?? item.name}
          decoding="async"
          draggable={false}
          data-ready={ready || undefined}
          onLoad={() => setLoadedUrl(url)}
          onError={() => {
            // Almost always an expired signature rather than a missing file, so
            // one silent re-sign is worth trying before showing an error.
            if (!retried.current) {
              retried.current = true;
              onReload(item.id);
              return;
            }
            setFailedUrl(url);
          }}
        />
      )}

      {state === "loading" && (
        <div className="fc-lightbox__status" aria-live="polite">
          <span className="fc-lightbox__spinner" aria-hidden="true" />
          <span>Loading photo…</span>
        </div>
      )}

      {state === "error" && (
        <div className="fc-lightbox__placeholder" data-state="failed">
          <strong>This photo didn&apos;t load</strong>
          <span>The link may have expired.</span>
          <button
            type="button"
            className="fc-lightbox__retry"
            onClick={() => {
              retried.current = false;
              setFailedUrl(null);
              onReload(item.id);
            }}
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Signed URLs and their sealed keys, kept across openings of the viewer.
 *
 * Only what the server said — never a decrypted object URL. Those are owned by
 * `mediaCache`, which counts who is displaying them and revokes on lock; a
 * second cache holding them here would keep photographs alive past a sign-out.
 *
 * ── Why entries expire ──
 *
 * A signed URL is only valid for a while — fifteen minutes for an original —
 * and this cache used to hold one for the lifetime of the tab. Displaying a
 * stale one merely reloads, because `onError` re-signs. But a URL that leaves
 * the page does not come back through `onError`: iOS's long-press "Save to
 * Photos" re-fetches it directly, and a signature that has expired since the
 * photograph was opened answers 403 — which the phone reports as having no
 * internet connection. Dropping an entry before it expires means the next
 * request signs a fresh one.
 */
interface CachedRef extends MediaRef {
  expiresAt: number;
}

const urlCache = new Map<string, CachedRef>();

/**
 * Discarded early, by a margin.
 *
 * The URL has to outlive not just the render but whatever the person does next
 * with it — a long press, a menu, a decision. Handing out one with thirty
 * seconds left is handing out one that will fail.
 */
const URL_EXPIRY_MARGIN_MS = 120_000;

function Icon({ path, filled = false }: { path: string; filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      data-filled={filled || undefined}
    >
      <path d={path} />
    </svg>
  );
}
